import { CURRENT_APP_HEADER_KEY } from "@/constants/current_app";
import {
    globalLoggerToken,
    growaveConfigurationToken,
    gwStorefrontAppInfoToken,
    isEmbeddedModeToken,
    localizationInfoProviderToken,
} from "@/tokens";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import {
    DependencyContainer,
    container,
    instanceCachingFactory,
    singleton,
} from "tsyringe";

import { ModuleInterface } from "@interfaces/ModuleInterface";

import {
    gwI18nToken,
    translationsApiClientToken,
    translationsApiServiceToken,
} from "./di_tokens";
import { GwI18n } from "./GwI18n/GwI18n";
import I18nApiLoaderFactory from "./GwI18n/i18nApiLoaderFactory";
import I18nCriticalLoaderFactory from "./GwI18n/I18nCriticalLoaderFactory";
import { I18nApiLoader } from "./GwI18n/loaders/I18nApiLoader/I18nApiLoader";
import TranslationsApiService from "./TranslationsApiService/TranslationsApiService";

type I18nModuleExports = {
    i18n: GwI18n;
    i18nCriticalLoaderFactory: I18nCriticalLoaderFactory;
    i18nApiLoaderFactory: I18nApiLoaderFactory;
    localizationInfoProvider: LocalizationInfoProviderInterface;
};

@singleton()
export class I18nModule implements ModuleInterface<I18nModuleExports> {
    exports: I18nModuleExports;
    public constructor(private readonly commonModule: CommonModule) {
        container.register(translationsApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new ApiClientWithMetrics(
                    new ApiClient(
                        `${
                            di.resolve(gwStorefrontAppInfoToken)
                                .translationsServiceBaseUrl
                        }/translations-service/v2`,
                        {
                            [CURRENT_APP_HEADER_KEY]: di.resolve(
                                gwStorefrontAppInfoToken
                            ).currentApp,
                        },
                        {},
                        di.resolve(globalLoggerToken)
                    ),
                    di.resolve(
                        AnalyticsEventsCollectorModule
                    ).exports.eventsCollector,
                    this.commonModule.exports.locationController,
                    di.resolve(
                        AnalyticsEventsCollectorModule
                    ).exports.eventsFactory
                );
            }),
        });

        const i18nModuleLogger = container
            .resolve(globalLoggerToken)
            .newLogger("I18nModule");

        const translationsApiService = new TranslationsApiService(
            container.resolve(translationsApiClientToken),
            container.resolve(growaveConfigurationToken),
            i18nModuleLogger
        );
        container.registerInstance(
            translationsApiServiceToken,
            translationsApiService
        );

        const i18n = new GwI18n(
            i18nModuleLogger,
            container.resolve(
                AnalyticsEventsCollectorModule
            ).exports.eventsCollector
        );
        const localizationInfoProvider = container.resolve(
            localizationInfoProviderToken
        );

        const i18nCriticalLoaderFactory = new I18nCriticalLoaderFactory(
            container.resolve(localizationInfoProviderToken),
            container.resolve(translationsApiServiceToken),
            container.resolve(isEmbeddedModeToken)
        );
        const i18nApiLoaderFactory = new I18nApiLoaderFactory(
            container.resolve(localizationInfoProviderToken),
            container.resolve(translationsApiServiceToken)
        );

        i18n.registerLoader(
            "common.MediaUploader",
            new I18nApiLoader(
                "common.MediaUploader",
                localizationInfoProvider.getGwI18nLocale(),
                translationsApiService
            )
        );
        i18n.registerLoader(
            "common.PostModal",
            new I18nApiLoader(
                "common.PostModal",
                localizationInfoProvider.getGwI18nLocale(),
                translationsApiService
            )
        );
        container.registerInstance(gwI18nToken, i18n);
        this.exports = {
            i18n,
            i18nCriticalLoaderFactory: i18nCriticalLoaderFactory,
            i18nApiLoaderFactory: i18nApiLoaderFactory,
            localizationInfoProvider: container.resolve(
                localizationInfoProviderToken
            ),
        };
    }
    registerProviders(di: DependencyContainer) {
        return {};
    }
}
